// drawer/components/TIProviderSummary.tsx
import { useTheme } from '@/app/ThemeContext';
import { Section } from '@/sections/threats/components';
import { getTIProviderMeta } from '@/sections/threats/constants';
import type { EnrichedVulnerability } from '@/sections/threats/types';

import { TIProviderRiskBadge } from './TIProviderRiskBadge';

interface TIProviderSummaryProps {
  threatIntel?: EnrichedVulnerability;
}

/**
 * Compact summary of TI provider scores for the Overview tab
 */
export function TIProviderSummary({ threatIntel }: TIProviderSummaryProps) {
  const { currentTheme } = useTheme();
  const providers = threatIntel?.ti_providers;
  if (!providers || providers.length === 0) return null;

  return (
    <Section title="Threat Intelligence">
      <div className="flex flex-wrap gap-4">
        {providers.map(provider => {
          const meta = getTIProviderMeta(provider.provider);
          const severity =
            provider.risk_score != null
              ? meta.getSeverity(provider.risk_score)
              : undefined;

          return (
            <div
              key={provider.provider}
              data-testid={`ti-summary-${provider.provider}`}
              className="flex items-center gap-3"
            >
              {meta.logo && (
                <img
                  src={`/icons/${currentTheme}/${meta.logo}`}
                  alt={meta.name}
                  className="size-5"
                />
              )}
              <span className="text-chariot-text-primary text-sm font-medium">
                {meta.name}
              </span>
              {provider.risk_score != null && severity && (
                <TIProviderRiskBadge
                  score={provider.risk_score}
                  severity={severity}
                />
              )}
              {provider.risk_label && (
                <span className="text-chariot-text-secondary text-xs">
                  {provider.risk_label}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}
